/**
 * `wayai files …` — path-addressed, versioned blobs inside a file type.
 *
 * Top-level and deliberately NOT WayAI's hub `resource` files: those are reached
 * through hub config-as-code (`wayai pull`/`push`) and the two file surfaces stay
 * separate. `files pull`/`files push` sync a file type as a local folder
 * (`../files-sync/`); they are data commands, not config-as-code.
 */

import { Command } from 'commander';
import { expected } from '../../lib/expected.js';
import { readFileSync, writeFileSync } from 'node:fs';
import { basename } from 'node:path';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { outputFormat, parseData, pathSegment, pathSegments, requireBase } from '../helpers.js';
import { confirm } from '../../lib/utils.js';
import { sanitizeTerminalText } from '../../lib/terminal-output.js';
import { fileWriteHeaders } from '../files-sync/headers.js';
import { pullFiles, pushFiles } from '../files-sync/sync.js';

interface DiffResponse {
  metadata_delta?: {
    content_changed?: boolean;
    size?: { from: number; to: number } | null;
    content_type?: { from: string; to: string } | null;
    path?: { from: string; to: string } | null;
    label?: { from: string; to: string } | null;
    metadata?: {
      added?: Record<string, unknown>;
      removed?: Record<string, unknown>;
      changed?: Record<string, { from: unknown; to: unknown }>;
    };
  };
  content?: { changed?: boolean; kind?: string; lines?: Array<{ op: string; text: string }> };
}

function renderFileDiff(
  fileType: string,
  filePath: string,
  from: string,
  to: string,
  d: DiffResponse,
): void {
  console.log(sanitizeTerminalText(`${fileType}/${filePath}: v${from} → v${to}`));
  const md = d.metadata_delta;
  if (md) {
    for (const f of ['path', 'size', 'content_type', 'label'] as const) {
      const change = md[f];
      if (change) console.log(sanitizeTerminalText(`  ${f}: ${JSON.stringify(change.from)} → ${JSON.stringify(change.to)}`));
    }
    const meta = md.metadata;
    for (const [k, v] of Object.entries(meta?.added ?? {})) {
      console.log(sanitizeTerminalText(`  metadata + ${k}: ${JSON.stringify(v)}`));
    }
    for (const k of Object.keys(meta?.removed ?? {})) console.log(sanitizeTerminalText(`  metadata - ${k}`));
    for (const [k, v] of Object.entries(meta?.changed ?? {})) {
      console.log(sanitizeTerminalText(`  metadata ~ ${k}: ${JSON.stringify(v.from)} → ${JSON.stringify(v.to)}`));
    }
  }
  const content = d.content;
  if (!content?.changed) {
    console.log('  content: unchanged');
    return;
  }
  if (content.kind === 'text' && content.lines) {
    console.log('');
    for (const l of content.lines) console.log(sanitizeTerminalText(`${l.op} ${l.text}`));
  } else {
    console.log(sanitizeTerminalText(`  content: changed (${content.kind})`));
  }
}

/**
 * The local filename a download without `--to` writes to.
 *
 * The remote path is tenant-authored and reaches `writeFileSync`, so the derived
 * name has to be a real filename. `basename` strips directories and trailing
 * slashes, but it still maps `/` onto `''` and `q3/..` onto `'..'` — writing to
 * nothing, or to the parent directory. Those demand an explicit `--to` rather
 * than resolving to a surprising target.
 */
export function downloadTarget(remotePath: string, to: string | undefined): string {
  if (to) return to;
  const derived = basename(remotePath);
  if (derived === '' || derived === '.' || derived === '..') {
    throw expected(
      `Cannot derive a local filename from "${remotePath}" — pass --to <local> to name it.`,
    );
  }
  return derived;
}

export function buildFilesCommand(): Command {
  const files = new Command('files').description(
    'Manage files (path-addressed, versioned content) within a file type',
  );

  files
    .command('put <file_type> <path>')
    .description(
      'Upload a local file to a path (e.g. wayai files put reports q3/summary.pdf --file ./summary.pdf)',
    )
    .requiredOption('--file <local>', 'Local file to upload')
    .option('--content-type <type>', 'MIME type', 'application/octet-stream')
    .option(
      '--if-match <hash>',
      "Write only if the file's content hash is still this one (its ETag, or content_sha256 from get --meta); refused with a conflict otherwise",
    )
    .option('--label <text>', 'Label for the content version this upload creates (printable ASCII)')
    .option('--metadata <json>', "The file's metadata, a JSON object (inline or @filename)")
    .action(async function (
      this: Command,
      fileType: string,
      filePath: string,
      opts: { file: string; contentType: string; ifMatch?: string; label?: string; metadata?: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      // Every header is checked before the file is read or a request made.
      const headers = fileWriteHeaders({
        ifMatch: opts.ifMatch,
        label: opts.label,
        metadata: opts.metadata === undefined ? undefined : parseData(opts.metadata, '--metadata'),
      });
      const body = readFileSync(opts.file);
      const client = await createDataClient();
      printOutput(
        await client.upload(
          `/v1/${base}/files/${pathSegment(fileType, 'file_type')}/${pathSegments(filePath)}`,
          body,
          opts.contentType,
          headers,
        ),
        outputFormat(this),
      );
    });

  files
    .command('get <file_type> <path>')
    .description(
      'Download a file (or --meta for metadata only). --version N reads a specific retained content version.',
    )
    // `--to`, not `--output`: the namespace declares `--output <format>`, and a
    // parent's option is consumed by the parent's own parse before a subcommand
    // ever sees the argument. A subcommand re-declaring the word would be
    // silently ignored — the download would land on the derived name while the
    // user watched their chosen filename be read as an output format.
    .option('--to <local>', 'Local file to save to (defaults to the file basename)')
    .option('--meta', 'Print metadata JSON instead of downloading the content')
    .option('--version <n>', 'Read a specific content version (default: latest)')
    .action(async function (
      this: Command,
      fileType: string,
      filePath: string,
      opts: { to?: string; meta?: boolean; version?: string },
    ) {
      const encoded = pathSegments(filePath);
      const versionQs =
        opts.version !== undefined ? `version=${encodeURIComponent(opts.version)}` : '';
      const base = pathSegment(requireBase(this), '--base');

      if (opts.meta) {
        const qs = ['meta=1', versionQs].filter(Boolean).join('&');
        const client = await createDataClient();
        printOutput(
          await client.request('GET', `/v1/${base}/files/${pathSegment(fileType, 'file_type')}/${encoded}?${qs}`),
          outputFormat(this),
        );
        return;
      }

      // Resolve the local target BEFORE the download so a path that cannot name
      // a file fails without spending the transfer.
      const out = downloadTarget(filePath, opts.to);
      const client = await createDataClient();
      const { bytes } = await client.download(
        `/v1/${base}/files/${pathSegment(fileType, 'file_type')}/${encoded}${versionQs ? `?${versionQs}` : ''}`,
      );
      writeFileSync(out, bytes);
      console.log(`Downloaded to ${out}`);
    });

  files
    .command('history <file_type> <path>')
    .description('List the content versions of a file (newest first)')
    .option('--limit <n>', 'Max versions to return')
    .option('--offset <n>', 'Pagination offset')
    .action(async function (
      this: Command,
      fileType: string,
      filePath: string,
      opts: { limit?: string; offset?: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const qs = new URLSearchParams({ history: '1' });
      if (opts.limit) qs.set('limit', opts.limit);
      if (opts.offset) qs.set('offset', opts.offset);
      printOutput(
        await client.request(
          'GET',
          `/v1/${base}/files/${pathSegment(fileType, 'file_type')}/${pathSegments(filePath)}?${qs}`,
        ),
        outputFormat(this),
      );
    });

  files
    .command('diff <file_type> <path>')
    .description('Diff two content versions of a file — metadata delta plus a text content diff')
    .requiredOption('--from <n>', 'Base version number')
    .requiredOption('--to <n>', 'Target version number')
    .action(async function (
      this: Command,
      fileType: string,
      filePath: string,
      opts: { from: string; to: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const qs = new URLSearchParams({ diff: '1', from: opts.from, to: opts.to });
      const data = await client.request<DiffResponse>(
        'GET',
        `/v1/${base}/files/${pathSegment(fileType, 'file_type')}/${pathSegments(filePath)}?${qs}`,
      );
      if (outputFormat(this) === 'json') {
        printOutput(data, 'json');
        return;
      }
      renderFileDiff(fileType, filePath, opts.from, opts.to, data);
    });

  files
    .command('list <file_type>')
    .description('List files by path prefix')
    .option('--prefix <path>', 'Path prefix filter (e.g. q3/)')
    .option('--depth <n>', 'Max folder depth below the prefix (1 = immediate children)')
    .option('--limit <n>', 'Max results')
    .option('--offset <n>', 'Pagination offset')
    .action(async function (
      this: Command,
      fileType: string,
      opts: { prefix?: string; depth?: string; limit?: string; offset?: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const qs = new URLSearchParams();
      if (opts.prefix) qs.set('prefix', opts.prefix);
      if (opts.depth) qs.set('depth', opts.depth);
      if (opts.limit) qs.set('limit', opts.limit);
      if (opts.offset) qs.set('offset', opts.offset);
      const q = qs.toString();
      printOutput(
        await client.request('GET', `/v1/${base}/files/${pathSegment(fileType, 'file_type')}${q ? `?${q}` : ''}`),
        outputFormat(this),
      );
    });

  files
    .command('mv <file_type> <from> <to>')
    .description('Rename/move a file (the blob is untouched)')
    .action(async function (this: Command, fileType: string, from: string, to: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('PATCH', `/v1/${base}/files/${pathSegment(fileType, 'file_type')}/${pathSegments(from)}`, {
          new_path: to,
        }),
        outputFormat(this),
      );
    });

  files
    .command('rm <file_type> <path>')
    .description('Delete a file')
    .option('-y, --yes', 'Skip confirmation prompt')
    .option(
      '--if-match <hash>',
      "Delete only if the file's content hash is still this one; refused with a conflict otherwise",
    )
    .action(async function (
      this: Command,
      fileType: string,
      filePath: string,
      opts: { yes?: boolean; ifMatch?: string },
    ) {
      const headers = fileWriteHeaders({ ifMatch: opts.ifMatch });
      if (
        !opts.yes &&
        !(await confirm(`Delete file "${fileType}/${filePath}"? This cannot be undone.`))
      ) {
        console.log('Aborted');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      await client.request(
        'DELETE',
        `/v1/${base}/files/${pathSegment(fileType, 'file_type')}/${pathSegments(filePath)}`,
        undefined,
        headers,
      );
      console.log('Deleted');
    });

  files
    .command('pull <file_type>')
    .description(
      'Pull a file type, or one --folder of it, into a local folder (default ./<file_type>[/<folder>]), recording each file\'s version for push. Local changes are kept; --overwrite replaces them',
    )
    .option('--folder <path>', 'Sync one folder of the file type (e.g. hr), not all of it')
    .option('--dir <local>', 'Local folder to sync into (default ./<file_type>, or ./<file_type>/<folder>)')
    .option('--overwrite', "Replace local changes with the base's copies")
    .option('-y, --yes', 'Skip the confirmation --overwrite asks for')
    .action(async function (
      this: Command,
      fileType: string,
      opts: { folder?: string; dir?: string; overwrite?: boolean; yes?: boolean },
    ) {
      const base = requireBase(this);
      await pullFiles({
        base,
        fileType,
        folder: opts.folder,
        dir: opts.dir,
        overwrite: opts.overwrite ?? false,
        yes: opts.yes ?? false,
      });
    });

  files
    .command('push <file_type>')
    .description(
      'Push the local changes of a folder pulled with `files pull` to the base, after showing the diff. A file changed, deleted or created on the base since your pull is a conflict, not written; an update or delete also carries the version pulled, so the base refuses it if the file changed meanwhile',
    )
    .option('--folder <path>', 'The folder of the file type the local folder was pulled from')
    .option('--dir <local>', 'Local folder to push (default ./<file_type>, or ./<file_type>/<folder>)')
    .option('--dry-run', 'Show the diff and push nothing')
    .option('--prune', 'Also delete files you deleted locally — only those unchanged on the base since your pull')
    .option('--label <text>', 'Label for every content version the push creates (printable ASCII)')
    .option('-y, --yes', 'Push without asking')
    .action(async function (
      this: Command,
      fileType: string,
      opts: { folder?: string; dir?: string; dryRun?: boolean; prune?: boolean; label?: string; yes?: boolean },
    ) {
      const base = requireBase(this);
      await pushFiles({
        base,
        fileType,
        folder: opts.folder,
        dir: opts.dir,
        dryRun: opts.dryRun ?? false,
        prune: opts.prune ?? false,
        label: opts.label,
        yes: opts.yes ?? false,
      });
    });

  files
    .command('mount <file_type>')
    .description(
      'Mint an S3 credential so any harness can mount this file type as a filesystem (s3fs, cloud mount SDKs). Read-only by default; the secret is shown once.',
    )
    .option('--expires-in <seconds>', 'Credential lifetime in seconds (default 30 days, max 90)')
    .option(
      '--mode <mode>',
      'read_only (default) or read_write — the whole-bucket mode when no --path is given',
    )
    .option(
      '--path <prefix>',
      'Confine to a path prefix (repeatable); optionally suffix :read_only|:read_write per prefix (e.g. drafts/:read_write)',
      (v: string, acc: string[]) => {
        acc.push(v);
        return acc;
      },
      [] as string[],
    )
    .action(async function (
      this: Command,
      fileType: string,
      opts: { expiresIn?: string; mode?: string; path: string[] },
    ) {
      const body: Record<string, unknown> = { file_type: fileType };
      if (opts.mode) {
        if (opts.mode !== 'read_only' && opts.mode !== 'read_write') {
          throw expected("--mode must be 'read_only' or 'read_write'.");
        }
        body.mode = opts.mode;
      }
      if (opts.path.length > 0) {
        body.paths = opts.path.map((p) => {
          const m = /^(.*):(read_only|read_write)$/.exec(p);
          return m ? { prefix: m[1], mode: m[2] } : { prefix: p, mode: opts.mode ?? 'read_only' };
        });
      }
      if (opts.expiresIn !== undefined) {
        const n = Number(opts.expiresIn);
        if (!Number.isFinite(n)) {
          throw expected('--expires-in must be a number of seconds.');
        }
        body.expires_in = n;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('POST', `/v1/${base}/s3-credentials`, body),
        outputFormat(this),
      );
    });

  return files;
}
